// src/hooks/useDismissOnOutside.ts

import { useEffect } from 'react';
import type { RefObject } from 'react';

/**
 * Closes a popover when the pointer goes down outside it, or on Escape.
 *
 * The alternative — a full-screen transparent div that swallows the next click —
 * also swallows clicks on the trigger, so pressing the button that opened the
 * menu could not close it again. Listening on the document leaves every element
 * on the page, the trigger included, clickable.
 *
 * Pointerdown rather than click: a menu should be gone before the thing under
 * the pointer reacts, not after.
 */
export interface DismissOptions {
  /** Off when the caller's notion of "outside" is subtler than a stray click. */
  onOutsideClick?: boolean;
  /** Off when the caller handles Escape itself (to return focus, say). */
  onEscape?: boolean;
  /**
   * Extra "inside" test for what no ref can cover — a trigger known only by the
   * rect it occupied when the popover opened, say. Runs before dismissal.
   */
  isInside?: (event: PointerEvent) => boolean;
}

export function useDismissOnOutside(
  /**
   * Everything that counts as inside. More than one because a popover rendered
   * through a portal is no longer a descendant of its trigger, and treating it
   * as outside would dismiss the menu before a click on one of its items landed.
   * Nulls are allowed so callers can pass optional refs without filtering.
   */
  refs: RefObject<HTMLElement | null> | Array<RefObject<HTMLElement | null> | null | undefined>,
  isOpen: boolean,
  onDismiss: () => void,
  { onOutsideClick = true, onEscape = true, isInside }: DismissOptions = {},
): void {
  useEffect(() => {
    if (!isOpen) return;

    const list = (Array.isArray(refs) ? refs : [refs]).filter(Boolean) as Array<RefObject<HTMLElement | null>>;
    const onPointerDown = (event: PointerEvent) => {
      if (!onOutsideClick) return;
      const target = event.target as Node;
      const inside = list.some((r) => r.current?.contains(target)) || isInside?.(event) === true;
      if (!inside) onDismiss();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (!onEscape || event.key !== 'Escape') return;
      event.stopPropagation();
      onDismiss();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onDismiss, onOutsideClick, onEscape, isInside]);
}
